/**
 * Single source of truth for the membership cadence tiers (Stripe-backed prices).
 *
 * WHY THIS EXISTS: every price surface (the cadence card, the desktop sidebar,
 * the mobile order summary, the add-ons step and the checkout hook) needs the
 * membership tier prices. If each calls `useProducts(...)` itself, a difference
 * in arguments (e.g. with vs without the market segment) yields a different
 * React-Query cache key, and a difference in load timing lets one surface resolve
 * the real Stripe amount while another falls back to the hardcoded default — so
 * two prices for the same plan can appear on screen at once. Routing every
 * surface through this one hook guarantees they share one cache key and one
 * resolved value, making same-screen divergence impossible.
 *
 * Membership is Swiss-only, so the tiers are read on the default (CH) market —
 * do NOT thread a market argument here or the cache key splits again.
 */
import { useProducts } from "@/components/product-step/useProducts";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import type { MembershipTier } from "@/hooks/createProductsQueryFn";

export interface UseMembershipTiersResult {
  tiers: MembershipTier[] | undefined;
  isLoading: boolean;
}

export const useMembershipTiers = (): UseMembershipTiersResult => {
  const language = useBookingFlowStore((s) => s.language);
  const query = useProducts(language);
  return { tiers: query.data?.membershipTiers, isLoading: query.isLoading };
};
